// src/pages/ProgramLandingPage.js (User App)
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom'; // useNavigate for potential redirects
import apiClient from '../apiClient'; // User App's API client
import Modal from '../components/Modal'; // The generic Modal component
import UserLogin from '../components/auth/UserLogin'; // The new Login form component
import UserRegister from '../components/auth/UserRegister'; // The new Register form component
import { useParticipantAuth } from '../context/ParticipantAuthContext'; // To check if already logged in

import '../styles/ProgramLandingPage.css';
import defaultProgramIcon from '../assets/icons/default-program-icon.svg';

const ProgramLandingPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { isAuthenticated, logout } = useParticipantAuth(); // Get auth state

    const programShortName = searchParams.get('p');

    const [programDetails, setProgramDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalView, setModalView] = useState('login'); // 'login' or 'register'

    useEffect(() => {
        if (!programShortName) {
            // No ?p= in URL. If we remembered the last program the user came from
            // (saved on a previous successful landing), redirect back to it so the
            // user lands on a real program page after logout / session-expired.
            const lastShortName = localStorage.getItem('lastProgramShortName');
            if (lastShortName) {
                navigate(`/?p=${lastShortName}`, { replace: true });
                return;
            }
            setError('No program specified. Please use a valid program link.');
            setIsLoading(false);
            return;
        }

        const fetchProgramDetails = async () => {
            setIsLoading(true);
            setError('');
            try {
                const response = await apiClient.get(`/public/programs/${programShortName}`);
                setProgramDetails(response.data);
                if (response.data?.short_name) {
                    localStorage.setItem('lastProgramShortName', response.data.short_name);
                }
            } catch (err) {
                const errorMsg = err.response?.data?.detail || err.message || 'Failed to load program details.';
                if (err.response?.status === 404) {
                    setError(`Program '${programShortName}' not found. Please check the link.`);
                } else {
                    setError(`Error: ${errorMsg}`);
                }
                setProgramDetails(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProgramDetails();
    }, [programShortName, navigate]);

    const openAuthModal = (view) => {
        setModalView(view);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleLoginSuccess = () => {
        closeModal();
        navigate('/profile'); // Navigate to profile on successful login
    };
    
    const handleRegisterSuccess = () => {
        // After successful registration, switch modal to login view
        // The UserRegister component already shows a message "Please login"
        setModalView('login'); 
        // Modal remains open, showing the login form.
    };

    const handleLogout = () => {
        logout();
        // Stay on the same page or navigate to home, program landing page will re-render with login/register buttons
        // navigate(`/?p=${programShortName}`); // Optional: force re-render or navigate
    };


    let iconSrc = defaultProgramIcon;
    if (programDetails && programDetails.logo_url) {
        if (programDetails.logo_url.startsWith('http://') || programDetails.logo_url.startsWith('https://')) {
            iconSrc = programDetails.logo_url;
        } else {
            const base = apiClient.defaults.baseURL.endsWith('/') ? apiClient.defaults.baseURL.slice(0, -1) : apiClient.defaults.baseURL;
            const logoPath = programDetails.logo_url.startsWith('/') ? programDetails.logo_url : `/${programDetails.logo_url}`;
            iconSrc = `${base}${logoPath}`;
        }
    }

    if (isLoading) {
        return <div className="container loading-container"><p>Loading program details...</p></div>;
    }

    if (error) {
        return <div className="container error-container"><h2>Error</h2><p>{error}</p></div>;
    }

    if (!programDetails) {
        return <div className="container"><p>Program details could not be loaded. Please try again or check the link.</p></div>;
    }

    return (
        <div className="container program-landing-container">
            <header className="program-header">
                <img src={iconSrc} alt={`${programDetails.name} Logo`} className="program-logo" />
                <h1>Welcome to {programDetails.name}!</h1>
                <p className="program-shortname-display">({programDetails.short_name})</p>
            </header>

            {programDetails.description && (
                <section className="program-description">
                    <h2>About This Program</h2>
                    <p>{programDetails.description}</p>
                </section>
            )}

            <section className="program-actions">
                {isAuthenticated ? (
                    <>
                        <p>You are currently logged in.</p>
                        <button onClick={() => navigate('/profile')} className="action-button primary-action">
                            Go to My Profile
                        </button>
                        <button onClick={handleLogout} className="action-button secondary-action">
                            Logout
                        </button>
                    </>
                ) : (
                    <>
                        <p>Ready to join or check your rewards?</p>
                        <button onClick={() => openAuthModal('register')} className="action-button register-button">
                            Register for {programDetails.name}
                        </button>
                        <button onClick={() => openAuthModal('login')} className="action-button login-button">
                            Login to {programDetails.name}
                        </button>
                    </>
                )}
            </section>
            
            <footer className="landing-footer">
                <p>Powered by ZappyRewards</p>
            </footer>

            {isModalOpen && programDetails && (
                <Modal 
                    isOpen={isModalOpen} 
                    onClose={closeModal} 
                    title={modalView === 'login' ? `Login to ${programDetails.name}` : `Register for ${programDetails.name}`}
                >
                    {modalView === 'login' ? (
                        <UserLogin
                            programId={programDetails.id}
                            programName={programDetails.name}
                            programShortName={programDetails.short_name}
                            onSwitchToRegister={() => setModalView('register')}
                            onLoginSuccess={handleLoginSuccess}
                        />
                    ) : (
                        <UserRegister
                            programId={programDetails.id}
                            programName={programDetails.name}
                            programShortName={programDetails.short_name}
                            onSwitchToLogin={() => setModalView('login')}
                            onRegisterSuccess={handleRegisterSuccess}
                        />
                    )}
                </Modal>
            )}
        </div>
    );
};

export default ProgramLandingPage;
